'use client'
import { Input, type InputProps } from '@/components/Forms'
import { useI18N } from '@/context/I18NContext'
import { forwardRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'

type Value = number | null

export interface InputNumberProps
	extends Omit<InputProps, 'value' | 'defaultValue' | 'type'> {
	name: string
	defaultValue?: Value
	fixedDecimalScale?: boolean
	max?: number | string
	min?: number | string
	decimals?: number
	thousands?: boolean
	allowNegative?: boolean
}

export const InputNumber = forwardRef<HTMLInputElement, InputNumberProps>(
	(
		{
			name,
			defaultValue,
			fixedDecimalScale = false,
			disabled,
			error,
			onChange,
			decimals = 2,
			thousands = true,
			allowNegative = true,
			...props
		},
		ref,
	) => {
		const { decimal, thousand } = getNumberSeparators()
		const { control } = useFormContext()

		return (
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<NumericFormat
						customInput={Input}
						role="textbox"
						aria-invalid={error ? 'true' : 'false'}
						getInputRef={ref}
						valueIsNumericString={true}
						disabled={disabled}
						allowNegative={allowNegative}
						defaultValue={defaultValue}
						value={field.value}
						thousandSeparator={thousand}
						decimalSeparator={decimal}
						decimalScale={decimals}
						fixedDecimalScale={fixedDecimalScale}
						inputMode={decimals === 0 ? 'numeric' : 'decimal'}
						onValueChange={({ floatValue }) => {
							field.onChange?.(floatValue)
						}}
						error={error}
						{...props}
					/>
				)}
			/>
		)
	},
)

export const getNumberSeparators = () => {
	const { language } = useI18N()
	const separators =
		language === 'en'
			? { decimal: '.', thousand: ',' }
			: { decimal: ',', thousand: ' ' }
	return separators
}
